import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/logo.webp";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Routes with a light background behind the navigation – force the
  // dark, semi-transparent (scrolled) style permanently so the nav stays
  // legible above white content.
  const lightBgRoutes = ["/kontakt", "/angebot-fassadensanierung", "/impressum", "/datenschutz"];
  const isLightBgPage = lightBgRoutes.includes(location.pathname);
  const forceDarkBar = isScrolled || isLightBgPage || isServicesOpen || isMobileMenuOpen;
  const useDarkText = false;

  const services = [
    { name: "Fassadensanierung & WDVS", link: "/fassadensanierung-wdvs-herford" },
    { name: "Innendämmung & Schimmelschutz", link: "/innendaemmung-schimmelschutz-herford" },
    { name: "Energetische Sanierung", link: "/energetische-sanierung-herford" },
    { name: "Malerarbeiten & Innensanierung", link: "/innensanierung-malerarbeiten-herford" },
    { name: "Badrenovierung & Fugenlos", link: "/badrenovierung-fugenlose-baeder-herford" },
    { name: "Edle Oberflächen & Böden", link: "/edle-oberflaechen-designboeden-herford" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateToAnchor = (id: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        forceDarkBar
          ? `bg-foreground backdrop-blur-xl border-b border-background/10 shadow-hard ${isScrolled ? "py-2.5" : "py-3.5"}`
          : "bg-foreground/70 backdrop-blur-md py-4"
      }`}
    >
      <div className="container mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src={logo}
              alt="STEIN Malerfachbetrieb"
              className={`transition-all duration-500 ${isScrolled ? "h-8" : "h-9"} ${useDarkText ? "invert" : ""}`}
              width={140}
              height={36}
            />
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-7">
            <Link to="/" className={`text-[11px] font-medium tracking-[0.2em] uppercase transition-all duration-300 ${useDarkText ? "text-foreground/70 hover:text-foreground" : "text-background/70 hover:text-background"}`}>
              Startseite
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button className={`py-2 text-[11px] font-medium tracking-[0.2em] uppercase transition-all duration-300 flex items-center gap-1 ${useDarkText ? "text-foreground/70 hover:text-foreground" : "text-background/70 hover:text-background"}`}>
                Leistungen
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${isServicesOpen ? "rotate-180" : ""}`} />
              </button>

              {isServicesOpen && (
                <div className="absolute top-full left-0 pt-2 z-[100]">
                  <div className="w-80 bg-foreground border border-background/10 rounded-lg shadow-2xl overflow-hidden">
                    {services.map((service, index) => (
                      <Link
                        key={index}
                        to={service.link}
                        className="group/item flex items-center justify-between w-full text-left px-5 py-3.5 text-sm font-medium text-background hover:bg-background/10 transition-all duration-200 border-b border-background/10 last:border-b-0"
                        onClick={() => setIsServicesOpen(false)}
                      >
                        <span>{service.name}</span>
                        <div className="w-1 h-1 bg-background rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/ueber-uns" className={`text-[11px] font-medium tracking-[0.2em] uppercase transition-all duration-300 ${useDarkText ? "text-foreground/70 hover:text-foreground" : "text-background/70 hover:text-background"}`}>
              Über uns
            </Link>

            <button
              onClick={() => navigateToAnchor("referenzen")}
              className={`text-[11px] font-medium tracking-[0.2em] uppercase transition-all duration-300 cursor-pointer ${useDarkText ? "text-foreground/70 hover:text-foreground" : "text-background/70 hover:text-background"}`}
            >
              Referenzen
            </button>

            <button
              onClick={() => navigateToAnchor("kontakt")}
              className={`text-[11px] font-medium tracking-[0.2em] uppercase transition-all duration-300 cursor-pointer ${useDarkText ? "text-foreground/70 hover:text-foreground" : "text-background/70 hover:text-background"}`}
            >
              Kontakt
            </button>

            <Button
              onClick={() => { setIsMobileMenuOpen(false); navigate("/angebot-fassadensanierung"); }}
              className={`font-medium px-5 h-9 rounded-md transition-all duration-300 hover:translate-y-[-1px] uppercase text-[11px] tracking-[0.2em] ${useDarkText ? "bg-foreground text-background hover:bg-foreground/90" : "bg-background text-foreground hover:bg-background/90"}`}
            >
              Angebotsrechner
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden transition-transform duration-300 hover:scale-110 ${useDarkText ? "text-foreground" : "text-background"}`}
            aria-label={isMobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-6 pb-6 space-y-4 animate-fade-in">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left text-base font-medium tracking-wider uppercase transition-colors text-background/80 hover:text-background py-3 border-b border-background/10"
            >
              Startseite
            </Link>

            <div>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="flex justify-between items-center w-full text-left text-base font-medium tracking-wider uppercase transition-colors text-background/80 hover:text-background py-3 border-b border-background/10"
              >
                Leistungen
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isServicesOpen ? "rotate-180" : ""}`} />
              </button>

              {isServicesOpen && (
                <div className="mt-2 ml-4 space-y-1">
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.link}
                      className="block w-full text-left px-4 py-2 text-sm font-light text-background/70 hover:text-background hover:bg-background/5 rounded-lg transition-colors"
                      onClick={() => { setIsServicesOpen(false); setIsMobileMenuOpen(false); }}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/ueber-uns"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left text-base font-medium tracking-wider uppercase transition-colors text-background/80 hover:text-background py-3 border-b border-background/10"
            >
              Über uns
            </Link>

            <button
              onClick={() => navigateToAnchor("referenzen")}
              className="block w-full text-left text-base font-medium tracking-wider uppercase transition-colors text-background/80 hover:text-background py-3 border-b border-background/10"
            >
              Referenzen
            </button>

            <button
              onClick={() => navigateToAnchor("kontakt")}
              className="block w-full text-left text-base font-medium tracking-wider uppercase transition-colors text-background/80 hover:text-background py-3 border-b border-background/10"
            >
              Kontakt
            </button>

            <Button
              onClick={() => { setIsMobileMenuOpen(false); navigate("/angebot-fassadensanierung"); }}
              className="w-full bg-background text-foreground hover:bg-background/90 font-medium uppercase text-sm tracking-wider rounded-lg"
            >
              Angebotsrechner
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};
